/**
 * /family/:treeSlug/wishes — "Occasions, Wishes & Remembrances".
 * 16 curated categories of joyful events, festivals, milestones and
 * memorial observances. Pick an occasion → compose & send a wish that is
 * saved permanently to a family member's life timeline.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import {
  ChevronLeft, Search, Cake, Baby, GraduationCap, Briefcase, Heart, Home,
  HandHeart, Sparkles, Church, Moon, Sun, Flower, Users, TreeDeciduous,
  Flower2, Flag, Gift,
} from "lucide-react";
import WishComposerModal from "@/components/family/WishComposerModal";

const ICONS = {
  cake: Cake, baby: Baby, graduation: GraduationCap, briefcase: Briefcase,
  heart: Heart, home: Home, "hand-heart": HandHeart, sparkles: Sparkles,
  church: Church, moon: Moon, sun: Sun, flower: Flower, users: Users,
  tree: TreeDeciduous, flower2: Flower2, flag: Flag,
};

const TONE = {
  joy:      { accent: "#B25D38", chipBg: "#FFF7ED" },
  festival: { accent: "#B45309", chipBg: "#FEF3C7" },
  care:     { accent: "#BE185D", chipBg: "#FDF2F8" },
  memorial: { accent: "#5B4A73", chipBg: "#F5F3FF" },
};

export default function OccasionsPage() {
  const { treeSlug } = useParams();
  const [sp] = useSearchParams();
  const [tree, setTree] = useState(null);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [composing, setComposing] = useState(null); // { occasion, member }
  const presetMemberId = sp.get("member");

  useEffect(() => {
    (async () => {
      try {
        const { data: t } = await http.get(`/family-trees/${treeSlug}`);
        const treeId = t.tree?.id || t.id;
        setTree({ ...(t.tree || t), id: treeId });
        const [mm, cat] = await Promise.all([
          http.get(`/family-trees/${treeId}/members`),
          http.get(`/occasions/catalog`),
        ]);
        setMembers(mm.data.items || []);
        setCategories(cat.data.categories || []);
      } catch {
        setCategories([]);
      }
    })();
  }, [treeSlug]);

  const presetMember = useMemo(() => members.find((m) => m.id === presetMemberId) || null, [members, presetMemberId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories
      .map((c) => ({ ...c, occasions: c.occasions.filter((o) => o.toLowerCase().includes(q) || c.title.toLowerCase().includes(q)) }))
      .filter((c) => c.occasions.length > 0);
  }, [categories, query]);

  const openComposer = (cat, label) => {
    setComposing({
      occasion: { category_key: cat.key, label, private_default: !!cat.private_default },
      member: presetMember,
    });
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8" data-testid="occasions-page">
      <Link to={`/family/${treeSlug}`} className="inline-flex items-center gap-2 text-sm hover:underline mb-4" style={{ color: "var(--wofy-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to {tree?.name || "family tree"}
      </Link>

      <div className="flex items-start gap-3 mb-2">
        <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#FFF7ED" }}>
          <Gift className="w-6 h-6" style={{ color: "#B25D38" }} />
        </span>
        <div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight" data-testid="occasions-title">Occasions, Wishes &amp; Remembrances</h1>
          <p className="text-sm mt-1 opacity-70">
            Joyful events, achievements, festivals, family milestones and memorial observances — pick an occasion and send a wish
            {presetMember ? <> to <b>{[presetMember.first_name, presetMember.last_name].filter(Boolean).join(" ")}</b></> : " to anyone in your family"}.
            Every wish is saved to their life timeline.
          </p>
        </div>
      </div>

      <div className="relative mt-5 mb-6 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
        <input
          className="wofy-input !pl-9"
          placeholder="Search occasions — Diwali, graduation, 50th birthday…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="occasions-search"
        />
      </div>

      {filtered.map((cat) => {
        const Icon = ICONS[cat.icon] || Sparkles;
        const tone = TONE[cat.tone] || TONE.joy;
        return (
          <section key={cat.key} className="mb-7" data-testid={`occasions-category-${cat.key}`}>
            <div className="flex items-center gap-2 mb-2.5">
              <Icon className="w-4.5 h-4.5 w-5 h-5" style={{ color: tone.accent }} />
              <h2 className="font-display text-xl" style={{ color: cat.tone === "memorial" ? tone.accent : "inherit" }}>{cat.title}</h2>
              {cat.private_default && (
                <span className="text-[10px] rounded-full px-2 py-0.5" style={{ background: tone.chipBg, color: tone.accent }}>shared privately by default</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cat.occasions.map((label) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => openComposer(cat, label)}
                  className="rounded-full border px-3 py-1.5 text-xs font-medium transition hover:-translate-y-0.5 hover:shadow-sm"
                  style={{ borderColor: "var(--wofy-border)", background: tone.chipBg, color: tone.accent }}
                  data-testid={`occasion-chip-${cat.key}-${label.slice(0, 20).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        );
      })}

      {filtered.length === 0 && categories.length > 0 && (
        <div className="text-sm opacity-60 py-8 text-center">No occasions match "{query}".</div>
      )}

      {composing && tree && (
        <WishComposerModal
          treeId={tree.id}
          members={members}
          member={composing.member}
          occasion={composing.occasion}
          onClose={() => setComposing(null)}
        />
      )}
    </div>
  );
}
